import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  Pagination,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";

import {
  AppstoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import ProductCard from "./ProductCard";

const { Text } = Typography;

const ProductBrowser = ({
  spareparts = [],
  cart = [],
  onAddToCart,
  onAddManyToCart,
  onQuantityChange,

  // Server-side search
  searchText = "",
  pagination,

  onSearch,
  onPageChange,

  isMobile,
}) => {
  /* ============================================================
     STATE
  ============================================================ */

  const [filter, setFilter] = useState("all");

  const [selectedIds, setSelectedIds] =
    useState([]);

  /*
   * Local search input.
   *
   * The actual API search is controlled by
   * the parent component.
   */
  const [searchValue, setSearchValue] =
    useState(searchText || "");

  /* ============================================================
     SYNC SEARCH FROM PARENT
  ============================================================ */

  useEffect(() => {
    setSearchValue(searchText || "");
  }, [searchText]);

  /* ============================================================
     CART IDS
  ============================================================ */

  const cartIds = useMemo(() => {
    return new Set(
      cart.map((item) =>
        String(item.sparepartId)
      )
    );
  }, [cart]);

  /* ============================================================
     FILTER PRODUCTS
  ============================================================
  
     Search and pagination are now handled by the backend.

     We only filter the CURRENT PAGE for:
       - All
       - Not in Cart
       - Already in Cart

  ============================================================ */

  const filteredProducts = useMemo(() => {
    return spareparts.filter((part) => {
      const isAdded = cartIds.has(
        String(part.id)
      );

      if (filter === "cart") {
        return isAdded;
      }

      if (filter === "available") {
        return !isAdded;
      }

      return true;
    });
  }, [
    spareparts,
    cartIds,
    filter,
  ]);

  /* ============================================================
     SELECTED PRODUCTS
  ============================================================ */

  const allVisibleSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((part) =>
      selectedIds.includes(part.id)
    );

  const someVisibleSelected =
    filteredProducts.some((part) =>
      selectedIds.includes(part.id)
    );

  /* ============================================================
     SELECT ONE
  ============================================================ */

  const toggleSelected = (product) => {
    setSelectedIds((current) => {
      const exists = current.includes(
        product.id
      );

      if (exists) {
        return current.filter(
          (id) => id !== product.id
        );
      }

      return [
        ...current,
        product.id,
      ];
    });
  };

  /* ============================================================
     SELECT ALL VISIBLE
  ============================================================ */

  const toggleAllVisible = () => {
    const visibleIds =
      filteredProducts.map(
        (part) => part.id
      );

    if (allVisibleSelected) {
      setSelectedIds((current) =>
        current.filter(
          (id) =>
            !visibleIds.includes(id)
        )
      );

      return;
    }

    setSelectedIds((current) => [
      ...new Set([
        ...current,
        ...visibleIds,
      ]),
    ]);
  };

  /* ============================================================
     ADD SELECTED
  ============================================================ */

  const addSelected = () => {
    if (
      selectedIds.length === 0
    ) {
      return;
    }

    const selectedProducts =
      spareparts.filter((part) =>
        selectedIds.includes(
          part.id
        )
      );

    if (
      selectedProducts.length === 0
    ) {
      return;
    }

    onAddManyToCart(
      selectedProducts
    );

    setSelectedIds([]);
  };

  /* ============================================================
     SEARCH
  ============================================================ */

  const handleSearch = (value) => {
    const search =
      value?.trim() || "";

    setSearchValue(search);

    /*
     * Parent calls:
     *
     * GET /api/purchase-orders/spareparts
     * ?page=1
     * &limit=50
     * &search=...
     */
    if (onSearch) {
      onSearch(search);
    }

    /*
     * Clear current selections
     * when changing search results.
     */
    setSelectedIds([]);
  };

  /* ============================================================
     CLEAR SEARCH
  ============================================================ */

  const handleClearSearch = () => {
    setSearchValue("");
    setSelectedIds([]);

    if (onSearch) {
      onSearch("");
    }
  };

  /* ============================================================
     FILTER CHANGE
  ============================================================ */

  const handleFilterChange = (
    value
  ) => {
    setFilter(value);
    setSelectedIds([]);
  };

  /* ============================================================
     PAGE CHANGE
  ============================================================ */

  const handlePageChange = (
    nextPage
  ) => {
    setSelectedIds([]);

    if (onPageChange) {
      onPageChange(nextPage);
    }
  };

  /* ============================================================
     CART ITEM LOOKUP
  ============================================================ */

  const getCartItem = (product) => {
    return cart.find(
      (item) =>
        String(
          item.sparepartId
        ) ===
        String(product.id)
    );
  };

  /* ============================================================
     TOTALS
  ============================================================ */

  const currentPageCount =
    filteredProducts.length;

  const totalParts =
    pagination?.total ??
    currentPageCount;

  const currentPage =
    pagination?.page || 1;

  const pageSize =
    pagination?.limit || 50;

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <Card
      title={
        <Space>
          <AppstoreOutlined />

          <span>
            Spare Parts
          </span>

          <Tag color="blue">
            {totalParts.toLocaleString(
              "en-US"
            )}
          </Tag>
        </Space>
      }
      style={{
        borderRadius: 14,
      }}
    >
      {/* ======================================================
          SEARCH
      ====================================================== */}

      <div
        style={{
          marginBottom: 16,
        }}
      >
        <Input.Search
          value={searchValue}
          placeholder="Search part name, part number or importer..."
          allowClear
          enterButton
          size={
            isMobile
              ? "middle"
              : "large"
          }
          onChange={(event) => {
            setSearchValue(
              event.target.value
            );
          }}
          onSearch={
            handleSearch
          }
          onClear={
            handleClearSearch
          }
        />
      </div>

      {/* ======================================================
          TOOLBAR
      ====================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: isMobile
            ? "stretch"
            : "center",
          flexDirection: isMobile
            ? "column"
            : "row",
          gap: 12,
          marginBottom: 14,
        }}
      >
        {/* FILTERS */}

        <Space
          wrap
          style={{
            width: isMobile
              ? "100%"
              : "auto",
          }}
        >
          <Select
            value={filter}
            onChange={
              handleFilterChange
            }
            style={{
              minWidth: 150,
            }}
            options={[
              {
                value: "all",
                label: "All Parts",
              },
              {
                value: "available",
                label: "Not in Cart",
              },
              {
                value: "cart",
                label: "Already in Cart",
              },
            ]}
          />

          <Checkbox
            checked={
              allVisibleSelected
            }
            indeterminate={
              !allVisibleSelected &&
              someVisibleSelected
            }
            onChange={
              toggleAllVisible
            }
          >
            Select visible
          </Checkbox>
        </Space>

        {/* ADD SELECTED */}

        {selectedIds.length >
          0 && (
          <Button
            type="primary"
            icon={
              <PlusOutlined />
            }
            onClick={
              addSelected
            }
            block={isMobile}
          >
            Add{" "}
            {selectedIds.length}{" "}
            Selected
          </Button>
        )}
      </div>

      {/* ======================================================
          SEARCH STATUS
      ====================================================== */}

      {searchValue && (
        <div
          style={{
            marginBottom: 12,
          }}
        >
          <Text type="secondary">
            Search results for{" "}
            <Text strong>
              "{searchValue}"
            </Text>
          </Text>
        </div>
      )}

      {/* ======================================================
          PRODUCTS
      ====================================================== */}

      <div
        style={{
          maxHeight: isMobile
            ? "none"
            : 700,

          overflowY: isMobile
            ? "visible"
            : "auto",

          paddingRight:
            isMobile
              ? 0
              : 4,
        }}
      >
        {filteredProducts.length ===
        0 ? (
          <Empty
            description={
              searchValue
                ? "No spare parts found"
                : filter === "cart"
                ? "No spare parts in cart"
                : filter ===
                  "available"
                ? "All visible spare parts are already in the cart"
                : "No spare parts available"
            }
            style={{
              padding:
                "50px 20px",
            }}
          />
        ) : (
          filteredProducts.map(
            (product) => (
              <ProductCard
                key={product.id}
                product={product}

                selected={selectedIds.includes(
                  product.id
                )}

                inCart={cartIds.has(
                  String(
                    product.id
                  )
                )}

                cartItem={getCartItem(
                  product
                )}

                onSelect={
                  toggleSelected
                }

                onAdd={
                  onAddToCart
                }

                onQuantityChange={
                  onQuantityChange
                }
              />
            )
          )
        )}
      </div>

      {/* ======================================================
          SERVER PAGINATION
      ====================================================== */}

      {pagination &&
        pagination.total >
          pagination.limit && (
          <div
            style={{
              display: "flex",
              justifyContent:
                "center",
              marginTop: 20,
            }}
          >
            <Pagination
              current={
                currentPage
              }
              pageSize={
                pageSize
              }
              total={
                pagination.total
              }
              showSizeChanger={
                false
              }
              showTotal={(
                total,
                range
              ) =>
                `${range[0]}-${range[1]} of ${total} spare parts`
              }
              onChange={
                handlePageChange
              }
              size={
                isMobile
                  ? "small"
                  : "default"
              }
            />
          </div>
        )}

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <div
        style={{
          marginTop: 14,
          textAlign: "center",
        }}
      >
        <Text type="secondary">
          {pagination ? (
            <>
              Showing{" "}
              {filteredProducts.length}{" "}
              of{" "}
              {pagination.total.toLocaleString(
                "en-US"
              )}{" "}
              spare parts
            </>
          ) : (
            <>
              Showing{" "}
              {filteredProducts.length}{" "}
              spare parts
            </>
          )}
        </Text>
      </div>
    </Card>
  );
};

export default ProductBrowser;